import { Injectable } from '@angular/core'
import { mergeById } from '../../../libs/filter.lib'
import { combineLatest, map, Observable, of, switchMap } from 'rxjs'
import {
  Product,
  ProductPageData,
  ProductScope,
  ProductsListingData,
  ProductsListingPage,
  ProductsListingSearchData,
  ProductsService,
  parseProductDataAttributes,
  parseProductsHistoricData,
} from '..'
import { CategoriesService, Category } from '../../categories'
import { Brand, BrandsService } from '../../brands'
import { Manufacturer, ManufacturersService } from '../../manufacturers'
import { ProductFamiliesService, ProductFamily } from '../../product-families'
import { Supplier, SuppliersService } from '../../suppliers'
import { Channel, ChannelsService } from '../../channels'
import { Tag, TagsService } from '../../tags'
import { Location, LocationsService } from '../../locations'
import { OrdersService } from '../../orders'
import { SupplierOrdersService } from '../../supplier-orders'
import { GoodsReceivesService } from '../../goods-receives'
import { PickingListsService } from '../../picking-lists'
import { parseProductPageKeys } from '../libs/products-page.lib'
import { Attribute, AttributesService } from '../../attributes'
import { uniqBy } from 'lodash'

@Injectable({
  providedIn: 'root',
})
export class ProductsListingRepository {
  // Stores
  private paginationLimit = 200

  constructor(
    private productsService: ProductsService,
    private attributesService: AttributesService,
    private ordersService: OrdersService,
    private supplierOrdersService: SupplierOrdersService,
    private goodsReceivesService: GoodsReceivesService,
    private pickingListsService: PickingListsService,
    private categoriesService: CategoriesService,
    private brandsService: BrandsService,
    private manufacturersService: ManufacturersService,
    private familiesService: ProductFamiliesService,
    private suppliersService: SuppliersService,
    private channelsService: ChannelsService,
    private tagsService: TagsService,
    private locationsService: LocationsService,
  ) {}

  /**
   * Search products page data by search params
   * @param searchData - the search data
   * @returns the observable for search products
   */
  searchProducts$(
    searchData: ProductsListingSearchData,
    listingData?: ProductsListingData,
    returnAll?: boolean,
  ): Observable<ProductsListingPage> {
    const needsPagination =
      searchData.params.limit !== undefined &&
      searchData.params.limit > this.paginationLimit

    return needsPagination
      ? this._paginateProducts$(searchData, listingData)
      : this._searchProducts$(searchData, listingData, returnAll)
  }

  /**
   * Load product variant family IDs
   * @param product - the product
   * @returns the observable for load product variant family IDs
   */
  getVariantFamilyIds$(product: Product): Observable<string[]> {
    if (product.productType === 'VIRTUAL') {
      return of([
        product._id,
        ...(product.variantsManagement?.childrenIds || []),
      ])
    }

    if (!product.parentId) {
      return of([])
    }

    return this.productsService
      .list$({ _id: product.parentId, fields: ['_id'] })
      .pipe(
        map((products) => products.map((p) => p._id)),
        map((productIds) => [product._id, ...productIds]),
      )
  }

  /**
   * Load order product IDs
   * @param orderId - the order ID
   * @returns the observable for load order product IDs
   */
  getOrderIds$(orderId: string): Observable<string[]> {
    return this.ordersService
      .read$(orderId)
      .pipe(map((order) => order.rows.map((r) => r.product._id)))
  }

  /**
   * Load supplier order product IDs
   * @param supplierOrderId - the supplier order ID
   * @returns the observable for load supplier-order product IDs
   */
  getSupplierOrderIds$(supplierOrderId: string): Observable<string[]> {
    return this.supplierOrdersService
      .read$(supplierOrderId)
      .pipe(
        map((supplierOrder) => supplierOrder.rows.map((r) => r.product._id)),
      )
  }

  /**
   * Load goods-receive product IDs
   * @param goodsReceiveId - the goods-receive ID
   * @returns the observable for load goods-receive product IDs
   */
  getGoodsReceiveIds$(goodsReceiveId: string): Observable<string[]> {
    return this.goodsReceivesService
      .read$(goodsReceiveId)
      .pipe(map((goodsReceive) => goodsReceive.rows.map((r) => r.product._id)))
  }

  /**
   * Load picking-list product IDs
   * @param pickingListId - the picking-list ID
   * @returns the observable for load picking-list product IDs
   */
  getPickingListIds$(pickingListId: string): Observable<string[]> {
    return this.pickingListsService
      .read$(pickingListId)
      .pipe(
        map((pickingList) => pickingList.missions.map((m) => m.product._id)),
      )
  }

  /**
   * Load products page external data
   * @param page - the products page
   * @param searchData - the search data
   * @returns the observable for load external data
   */
  getPageExternalData$(
    page: ProductsListingPage,
    searchData: ProductsListingSearchData,
    listingData?: ProductsListingData,
  ): Observable<ProductsListingPage> {
    const extDataKeys = parseProductPageKeys(page)
    const obs$: Partial<{
      attributes: Observable<Attribute[]>
      brands: Observable<Brand[]>
      categories: Observable<Category[]>
      channels: Observable<Channel[]>
      families: Observable<ProductFamily[]>
      kits: Observable<Product[]>
      locations: Observable<Location[]>
      manufacturers: Observable<Manufacturer[]>
      parents: Observable<Product[]>
      tags: Observable<Tag[]>
      suppliers: Observable<Supplier[]>
    }> = {}
    const fields = searchData.fields

    // Attributes
    const attributeFields = fields.filter((f) => f.startsWith('attribute_'))
    if (attributeFields.length) {
      const existingAttributes = listingData?.attributes || []
      const requestedCodes = attributeFields.map((f) =>
        f.replace('attribute_', ''),
      )
      const hasAllAttributes = requestedCodes.every((code) =>
        existingAttributes.some((a) => a.code === code),
      )

      if (!hasAllAttributes) {
        obs$.attributes = this.attributesService
          .list$({ code: requestedCodes })
          .pipe(
            map((fetched) =>
              uniqBy([...existingAttributes, ...fetched], 'code'),
            ),
          )
      } else {
        obs$.attributes = of(existingAttributes)
      }
    }

    // Brands
    if (fields.includes('brandId') && extDataKeys.brandsIds) {
      const existingBrands = listingData?.brands || []
      const hasAllBrands = extDataKeys.brandsIds.every((id) =>
        existingBrands.some((brand) => brand._id === id),
      )

      if (!hasAllBrands) {
        obs$.brands = this.brandsService.store$(
          extDataKeys.brandsIds,
          existingBrands,
        )
      } else {
        obs$.brands = of(existingBrands)
      }
    }

    // Categories
    if (fields.includes('categories') && extDataKeys.categoriesIds) {
      const existingCategories = listingData?.categories || []
      const hasAllCategories = extDataKeys.categoriesIds.every((id) =>
        existingCategories.some((brand) => brand._id === id),
      )

      if (!hasAllCategories) {
        obs$.categories = this.categoriesService.store$(
          extDataKeys.categoriesIds,
          existingCategories,
        )
      } else {
        obs$.categories = of(existingCategories)
      }
    }

    // Manufacturers
    if (fields.includes('manufacturerId') && extDataKeys.manufacturersIds) {
      const existingManufacturers = listingData?.manufacturers || []
      const hasAllManufacturers = extDataKeys.manufacturersIds.every((id) =>
        existingManufacturers.some((manufacturer) => manufacturer._id === id),
      )

      if (!hasAllManufacturers) {
        obs$.manufacturers = this.manufacturersService.store$(
          extDataKeys.manufacturersIds,
          existingManufacturers,
        )
      } else {
        obs$.manufacturers = of(existingManufacturers)
      }
    }

    // Parents
    if (fields.includes('parentId') && extDataKeys.parentsIds) {
      const existingParents = listingData?.parents || []
      const hasAllParents = extDataKeys.parentsIds.every((id) =>
        existingParents.some(
          (parent) => parent._id === id && !!parent.SKU?.length,
        ),
      )

      if (!hasAllParents) {
        obs$.parents = this.productsService.store$(
          extDataKeys.parentsIds,
          existingParents,
        )
      } else {
        obs$.parents = of(existingParents)
      }
    }

    // Kits
    if (fields.includes('kitIds') && extDataKeys.kitsIds) {
      const existingKits = listingData?.kits || []
      const hasAllKits = extDataKeys.kitsIds.every((id) =>
        existingKits.some((kit) => kit._id === id),
      )

      if (!hasAllKits) {
        obs$.kits = this.productsService.store$(
          extDataKeys.kitsIds,
          existingKits,
        )
      } else {
        obs$.kits = of(existingKits)
      }
    }

    // Channels
    if (
      fields.includes('externalSKUs.channelId') &&
      extDataKeys['externalSKUs.channelId']
    ) {
      const existingChannels = listingData?.channels || []
      const hasAllChannels = extDataKeys['externalSKUs.channelId'].every((id) =>
        existingChannels.some((channel) => channel._id === id),
      )

      if (!hasAllChannels) {
        obs$.channels = this.channelsService.store$(
          extDataKeys['externalSKUs.channelId'],
          existingChannels,
        )
      } else {
        obs$.channels = of(existingChannels)
      }
    }

    // Suppliers
    if (
      (fields.includes('suppliers.supplierId') ||
        fields.includes('suppliers._id')) &&
      extDataKeys.suppliersIds
    ) {
      const existingSuppliers = listingData?.suppliers || []
      const hasAllSuppliers = extDataKeys.suppliersIds.every((id) =>
        existingSuppliers.some((supplier) => supplier._id === id),
      )

      if (!hasAllSuppliers) {
        obs$.suppliers = this.suppliersService.store$(
          extDataKeys.suppliersIds,
          existingSuppliers,
        )
      } else {
        obs$.suppliers = of(existingSuppliers)
      }
    }

    // Tags
    if (fields.includes('tags') && extDataKeys.tagsIds) {
      const existingTags = listingData?.tags || []
      const filteredTags = extDataKeys.tagsIds.filter((t) => !t.startsWith('#'))
      const hasAllTags = filteredTags.every((tag) =>
        existingTags.some((existingTag) => existingTag.value === tag),
      )

      if (!hasAllTags) {
        obs$.tags = this.tagsService
          .store$(filteredTags, existingTags, 'value', 'products')
          .pipe(map((tags) => uniqBy([...tags], 'value')))
      } else {
        obs$.tags = of(uniqBy([...existingTags], 'value'))
      }
    }

    // Families
    if (fields.includes('family.code') && extDataKeys.familiesIds) {
      const existingFamilies = listingData?.families || []
      const hasAllFamilies = extDataKeys.familiesIds.every((id) =>
        existingFamilies.some((family) => family._id === id),
      )

      if (!hasAllFamilies) {
        obs$.families = this.familiesService.store$(
          extDataKeys.familiesIds,
          existingFamilies,
          'code',
        )
      } else {
        obs$.families = of(existingFamilies)
      }
    }

    // Sites
    if (fields.includes('sites') && extDataKeys.locationsIds) {
      const existingLocations = listingData?.locations || []
      const hasAllLocations = extDataKeys.locationsIds.every((id) =>
        existingLocations.some((loc) => loc._id === id),
      )

      if (!hasAllLocations) {
        obs$.locations = this.locationsService.store$(
          extDataKeys.locationsIds,
          existingLocations,
        )
      } else {
        obs$.locations = of(existingLocations)
      }
    }

    // Historic quantity
    const needsHistoric =
      searchData.historicData?.date !== undefined &&
      searchData.historicData?.warehouseId !== undefined

    // Create a base observable: either historic data or plain data
    const data$ = needsHistoric
      ? this.getPageHistoricQuantities$(
          page,
          searchData.historicData!.warehouseId!,
          searchData.historicData!.date!,
        )
      : of(page.data)

    if (!Object.keys(obs$).length) {
      return data$.pipe(
        map((resolvedData) => ({ data: resolvedData }) as ProductsListingPage),
      )
    }

    return data$.pipe(
      switchMap((resolvedData) =>
        combineLatest(obs$).pipe(
          map((results) => {
            const extData: ProductsListingData = {}
            if (results.attributes) extData.attributes = results.attributes
            if (results.brands) extData.brands = results.brands
            if (results.categories) extData.categories = results.categories
            if (results.channels) extData.channels = results.channels
            if (results.families) extData.families = results.families
            if (results.kits) extData.kits = results.kits
            if (results.locations) extData.locations = results.locations
            if (results.manufacturers)
              extData.manufacturers = results.manufacturers
            if (results.parents) extData.parents = results.parents
            if (results.tags) extData.tags = results.tags
            if (results.suppliers) extData.suppliers = results.suppliers

            let enrichedData = resolvedData

            const attributesToParse =
              results.attributes ?? listingData?.attributes

            if (attributesToParse?.length) {
              enrichedData = this.parseProductsPageAttributes(
                {
                  data: enrichedData,
                  totalCount: page.totalCount ?? enrichedData.length,
                },
                searchData.scope,
                attributesToParse,
              )
            }

            if (results.parents) {
              enrichedData = this.parseProductsPageParents(
                enrichedData,
                results.parents,
              )
            }
            if (results.kits) {
              enrichedData = this.parseProductsPageKits(
                enrichedData,
                results.kits,
              )
            }
            return {
              data: enrichedData,
              totalCount: page.totalCount ?? enrichedData.length,
              extData,
            }
          }),
        ),
      ),
    )
  }

  parseProductsPageAttributes(
    page: ProductsListingPage,
    scope: ProductScope | undefined,
    attributes: Attribute[] | undefined,
  ): ProductPageData[] {
    if (!scope || !attributes) {
      return page.data
    }

    return page.data.map((p) =>
      parseProductDataAttributes(p, attributes, scope),
    )
  }

  // Add these two parse functions back (e.g. in products-page.lib.ts)
  parseProductsPageParents(
    data: ProductPageData[],
    parents: Product[],
  ): ProductPageData[] {
    return data.map((p) => ({
      ...p,
      parent: parents.find((m) => m._id === p.parentId),
    }))
  }

  parseProductsPageKits(
    data: ProductPageData[],
    products: Product[],
  ): ProductPageData[] {
    return data.map((p) => ({
      ...p,
      kits: products.filter((k) => p.kitIds?.includes(k._id)),
    }))
  }

  /**
   * Load products page historic quantities
   * @param page - the product page
   * @param warehouseId - the warehouse ID
   * @param date - the date
   * @returns the observable for load products page data
   */
  getPageHistoricQuantities$(
    page: ProductsListingPage,
    warehouseId: string,
    date: string,
  ): Observable<ProductPageData[]> {
    const productIds = page.data.map((p) => p._id)
    return this.productsService
      .historicQuantities$(productIds, warehouseId, date)
      .pipe(
        map((historicQuantities) => {
          return parseProductsHistoricData(page, historicQuantities)
        }),
      )
  }

  /**
   * Load products page historic quantities
   * @param page - the product page
   * @param warehouseId - the warehouse ID
   * @param date - the date
   * @returns the observable for load products page data
   */
  async getPageHistoricQuantitiesPromise$(
    page: ProductsListingPage,
    warehouseId: string,
    date: string,
  ): Promise<ProductPageData[]> {
    const productIds = page.data.map((p) => p._id)
    return new Promise((resolve, reject) => {
      this.productsService
        .historicQuantities$(productIds, warehouseId, date)
        .subscribe({
          next: (data) => {
            resolve(parseProductsHistoricData(page, data))
          },
          error: (error) => {
            reject(error)
          },
        })
    })
  }

  // Utilities
  private _searchProducts$(
    searchData: ProductsListingSearchData,
    listingData?: ProductsListingData,
    returnAll?: boolean,
  ): Observable<ProductsListingPage> {
    return this.productsService.search$(searchData.params, returnAll).pipe(
      switchMap((page) =>
        this.getPageExternalData$(page, searchData, listingData).pipe(
          map((extData) => ({
            data: extData.data,
            totalCount: extData.totalCount,
            extData: extData.extData,
          })),
        ),
      ),
    )
  }

  private _paginateProducts$(
    searchData: ProductsListingSearchData,
    listingData?: ProductsListingData,
  ): Observable<ProductsListingPage> {
    const totalCount = searchData.params.limit || this.paginationLimit
    const offsets = Array.from(
      { length: Math.ceil(totalCount / this.paginationLimit) },
      (_, i) => i * this.paginationLimit,
    )

    return offsets.reduce(
      (acc$, offset) =>
        acc$.pipe(
          switchMap((accumulated) =>
            this._searchProducts$(
              {
                ...searchData,
                params: {
                  ...searchData.params,
                  limit: this.paginationLimit,
                  offset,
                },
              },
              accumulated.extData,
            ).pipe(
              map((res) => ({
                totalCount: res.totalCount ?? accumulated.totalCount,
                data: uniqBy([...accumulated.data, ...res.data], '_id'),
                extData: this._mergeListingExtData(
                  accumulated.extData,
                  res.extData,
                ),
              })),
            ),
          ),
        ),
      of({
        totalCount: 0,
        data: [],
        extData: listingData,
      } as ProductsListingPage),
    )
  }

  private _mergeListingExtData(
    prev: ProductsListingData | undefined,
    next: ProductsListingData | undefined,
  ): ProductsListingData | undefined {
    if (!prev && !next) return undefined

    const a = prev ?? {}
    const b = next ?? {}

    return {
      attributes: uniqBy(
        [...(a.attributes ?? []), ...(b.attributes ?? [])],
        'code',
      ),
      brands: mergeById(a.brands, b.brands),
      categories: mergeById(a.categories, b.categories),
      channels: mergeById(a.channels, b.channels),
      families: mergeById(a.families, b.families),
      kits: mergeById(a.kits, b.kits),
      locations: mergeById(a.locations, b.locations),
      manufacturers: mergeById(a.manufacturers, b.manufacturers),
      parents: mergeById(a.parents, b.parents),
      suppliers: mergeById(a.suppliers, b.suppliers),
      tags: uniqBy([...(a.tags ?? []), ...(b.tags ?? [])], 'value'), // ← fix
    }
  }

  private _parsePages$(
    searchData: ProductsListingSearchData,
  ): Observable<ProductsListingPage>[] {
    const observables$: Observable<ProductsListingPage>[] = []
    const totalCount = searchData.params.limit || this.paginationLimit
    let offset = 0

    while (offset <= totalCount) {
      observables$.push(
        this._searchProducts$({
          ...searchData,
          params: {
            ...searchData.params,
            limit: this.paginationLimit,
            offset,
          },
        }),
      )
      offset += this.paginationLimit
    }

    return observables$
  }
}
